import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { retry } from "rxjs/operators";
import { UserContextProvider } from "../context/user-context-provider";
import { AdminPortalAPIHttpClient } from "../http/v2/AdminPortalAPIHttpClient";
import { NotificationService } from "../../utilities/PortalNotificationUtility";

export enum LogLevel {
    DEBUG = 'DEBUG',
    INFO = 'INFO',
    ERROR = 'ERROR',
}

export interface Log {
    level: LogLevel;
    message: string;
}

export interface LogParameters {
    message: string;
    level?: LogLevel;
    error?: any;
}

export interface SessionInformation {
    accountId: string;
    url: string;
}

@Injectable({ providedIn: 'root' })
export class Logger {

    private static readonly MAX_RETRY_ATTEMPTS = 3;

    constructor(
        private adminPortalApiHttpClient: AdminPortalAPIHttpClient,
        private router: Router,
        private userContextProvider: UserContextProvider,
        private notificationService: NotificationService
    ) { }

    log({ message, level = LogLevel.INFO, error }: LogParameters): void {
        const timestamp = new Date().toUTCString();
        const sessionInformation = this.getSessionInformation();

        const logEntry = this.buildLog(message, level, sessionInformation, timestamp, error);

        this.printToConsole(logEntry, error);

        if (this.shouldTransportLog(logEntry)) {
            this.transportLog(logEntry, Logger.MAX_RETRY_ATTEMPTS);
        }
    }

    private buildLog(message: string, level: LogLevel, sessionInformation: SessionInformation, timestamp: string, error?: any): Log {
        const errorMessage = error ? ` Error: ${error}` : '';
        return {
            level: level,
            message: `${timestamp} Account ID: ${sessionInformation.accountId} URL: ${sessionInformation.url} - ${message}${errorMessage}`,
        }
    }

    private printToConsole(logEntry: Log, error?: any) {
        switch (logEntry.level) {
            case LogLevel.DEBUG:
                console.debug(logEntry.message);
                break;
            case LogLevel.INFO:
                console.log(logEntry.message);
                break;
            case LogLevel.ERROR:
                console.error(logEntry.message, error);
                break;
            default:
                console.log(logEntry.message);
        }
    }

    private shouldTransportLog(logEntry: Log): boolean {
        return logEntry.level !== LogLevel.DEBUG;
    }

    private transportLog(logEntry: Log, retryAttempts: number) {
        this.adminPortalApiHttpClient.post('/log', logEntry).pipe(
            retry(Logger.MAX_RETRY_ATTEMPTS - retryAttempts)).subscribe({
                next: (response) => console.debug('Log sent successfully: ', response, logEntry),
                error: (e) => this.handleTransportError.bind(this, e, logEntry, retryAttempts)
            });
    }

    private handleTransportError(error: any, logEntry: Log, retryAttempts: number) {
        if (retryAttempts < Logger.MAX_RETRY_ATTEMPTS) {
            console.warn(`Retrying log attempt ${retryAttempts + 1} after error:`, error);
            this.transportLog(logEntry, retryAttempts + 1);
        } else {
            console.error('Max retry attempts reached. Error sending log:', error);
        }
    }

    getSessionInformation(): SessionInformation {
        return {
            accountId: this.userContextProvider.account?.account_id,
            url: this.router.url
        }
    }

    handleError(error: any, message: string, logMessage: string) {
        this.notificationService.showNotification({ message: message });
        this.log({ 
            message: logMessage, 
            level: LogLevel.ERROR,
            error: error
        });
    }
}